import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import { ROOMS } from "../config/rooms";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Progress } from "../components/ui/progress";
import { ScrollArea } from "../components/ui/scroll-area";
import compressImage from "../lib/compressImage";
import ImageCropDialog from "../components/ImageCropDialog";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Loader2,
  Sparkles,
  User,
  Shield,
  MapPin,
  Backpack,
  Sun,
  Utensils,
  Users,
  MessageCircle,
  PartyPopper,
  Heart,
  Mountain,
  Dumbbell,
  Music,
  Briefcase,
  Palette,
  Leaf,
  Clock,
  Camera,
  FileText,
  ClipboardCheck
} from "lucide-react";

// Format date to DD/MM/YYYY
const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

// Phone numbers must include an international country code, e.g. +34 612 345 678
const isValidIntlPhone = (v) => /^\+\d{6,15}$/.test((v || "").replace(/[\s\-().]/g, ""));

// Pill-style single-choice selector
const ChoicePills = ({ options, value, onChange, testid }) => (
  <div className="flex gap-2 flex-wrap">
    {options.map((opt) => (
      <button
        key={String(opt.value)}
        type="button"
        onClick={() => onChange(opt.value)}
        className={`px-4 py-2.5 rounded-xl border text-sm font-medium transition-colors ${
          value === opt.value
            ? "bg-[#E07A5F] border-[#E07A5F] text-white"
            : "bg-white border-stone-200 text-[#3D4035]/60 hover:border-stone-300"
        }`}
        data-testid={`${testid}-${String(opt.value)}`}
      >
        {opt.label}
      </button>
    ))}
  </div>
);

const YNM_OPTIONS = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
  { value: "maybe", label: "Maybe" }
];

const STAY_TRANSPORT_OPTIONS = [
  { value: "car", label: "Car" },
  { value: "scooter", label: "Scooter" },
  { value: "none", label: "No vehicle" }
];

const STEPS = [
  { id: "welcome", title: "Welcome", icon: Sparkles },
  { id: "profile", title: "Profile", icon: User },
  { id: "rules", title: "House Rules", icon: Shield },
  { id: "arrival", title: "Arrival", icon: MapPin },
  { id: "legal", title: "Legal & ID", icon: FileText },
  { id: "stay", title: "Reason for Stay", icon: ClipboardCheck },
  { id: "rhythm", title: "Your Rhythm", icon: Clock },
  { id: "lifestyle", title: "Lifestyle", icon: Heart },
  { id: "packing", title: "Packing", icon: Backpack },
  { id: "activities", title: "Activities", icon: Sun },
  { id: "dietary", title: "Dietary", icon: Utensils },
  { id: "community", title: "Community Intro", icon: Users },
  { id: "whatsapp", title: "WhatsApp", icon: MessageCircle },
  { id: "final", title: "Confirmation", icon: PartyPopper }
];

// Volunteers skip the contract / arrival / legal / packing steps — they're here
// to help, not to sign a paying-guest contract. They still get every "fun" step
// (profile, rhythm, lifestyle, activities, dietary, community intro, WhatsApp).
const VOLUNTEER_SKIP_IDS = new Set(["arrival", "legal", "stay", "packing"]);

// RD-ley 26/2026: a temporary (temporada) let must state a real, documentable
// reason the guest is living away from their habitual home — otherwise it is
// legally treated as a permanent residence. These are the accepted causes.
const STAY_REASONS = [
  { value: "work_relocation", label: "Work — relocation or temporary posting" },
  { value: "remote_work", label: "Remote work away from my home base" },
  { value: "studies", label: "Studies or a training course" },
  { value: "medical", label: "Medical treatment or recovery" },
  { value: "seasonal_work", label: "Seasonal work" },
  { value: "family", label: "Family reasons / caring for a relative" },
  { value: "other", label: "Other (I'll explain below)" },
];

const OnboardingWizard = () => {
  const { user, token, updateUser, fetchUser } = useAuth();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);

  // Volunteers see a trimmed wizard: skip the contract / arrival / legal / packing
  // steps so they only do the fun community/profile/rhythm/activities stuff.
  const isVolunteer = user?.is_volunteer === true;
  useEffect(() => {
    if (user?.guest_type === "retreat" && !user?.onboarding?.final_confirmed) navigate("/retreat-welcome", { replace: true });
  }, [user, navigate]);
  const steps = useMemo(
    () => (isVolunteer ? STEPS.filter((s) => !VOLUNTEER_SKIP_IDS.has(s.id)) : STEPS),
    [isVolunteer],
  );

  // Form states
  const [profile, setProfile] = useState({
    name: user?.name || "",
    preferred_name: "",
    date_of_birth: "",
    emergency_contact_name: "",
    emergency_contact_phone: "",
    home_address: "",
    social_link: "",
    nationality: "",
    occupation: "",
    current_project: "",
    fun_fact: "",
    phone: "",
    avatar: "",
    social_media_ok: true,
    vibe: "",
    coffee_order: "",
    hidden_talent: "",
    ask_me_about: "",
    song_on_repeat: "",
    bringing_pet: false,
    pet_type: "",
    pet_name: "",
    pet_breed: "",
    pet_photo: "",
    // Support for multiple pets (set by admin)
    pets: []
  });

  // Avatar upload — user positions/crops the photo before it's stored, so faces aren't auto-cut.
  const [avatarCropFile, setAvatarCropFile] = useState(null);
  const handleAvatarUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 12 * 1024 * 1024) {
      toast.error("Image must be less than 12MB");
      return;
    }
    setAvatarCropFile(file);
    e.target.value = "";
  };
  const uploadAvatar = async (croppedFile) => {
    try {
      const compressed = await compressImage(croppedFile);
      const form = new FormData();
      form.append("file", compressed);
      const res = await axios.post(`${API}/upload/image`, form, { headers });
      setProfile((prev) => ({ ...prev, avatar: res.data.url }));
      toast.success("Photo uploaded!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Upload failed — try a JPG or PNG");
    } finally {
      setAvatarCropFile(null);
    }
  };

  // Handle pet photo upload
  const handlePetPhotoUpload = async (e, petIndex = null) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 12 * 1024 * 1024) {
      toast.error("Image must be less than 12MB");
      return;
    }
    try {
      const compressed = await compressImage(file);
      const form = new FormData();
      form.append("file", compressed);
      const res = await axios.post(`${API}/upload/image`, form, { headers });
      if (petIndex !== null) {
        setProfile((prev) => {
          const newPets = [...prev.pets];
          newPets[petIndex] = { ...newPets[petIndex], photo: res.data.url };
          return { ...prev, pets: newPets };
        });
      } else {
        setProfile((prev) => ({ ...prev, pet_photo: res.data.url }));
      }
      toast.success("Photo uploaded!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Upload failed — try a JPG or PNG");
    }
  };
  
  const [arrival, setArrival] = useState({
    arrival_date: "",
    departure_date: "",
    room_name: "",
    arrival_time: "",
    transport_method: "",
    pickup_requested: false,
    flight_number: "",
    notes: "",
    guests_during_stay: "",
    accommodation_help: "",
    stay_transport: "",
    food_delivery_ack: false
  });

  const [intake, setIntake] = useState({
    passport_number: "",
    visa_info: "",
    id_photo_front: "",
    id_photo_back: "",
    stay_reason: "",
    stay_reason_detail: "",
    stay_departure_date: "",
    why_savi: "",
    coliving_experience: "",
    daily_routine: "",
    remote_work: null,
    working_hours: "",
    work_needs: "",
    workshops: "",
    passions: "",
    volunteering: null,
    music_preference: "",
    social_events: ""
  });
  const [uploadingId, setUploadingId] = useState(null);
  const currentIconRef = useRef(null);

  // Keep the current step icon visible in the scrollable indicator row (mobile)
  useEffect(() => {
    currentIconRef.current?.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
  }, [currentStep]);

  // Upload an ID/passport photo and store its URL
  const handleIdUpload = async (e, side) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 12 * 1024 * 1024) {
      toast.error("Image must be less than 12MB");
      return;
    }
    setUploadingId(side);
    try {
      const compressed = await compressImage(file);
      const form = new FormData();
      form.append("file", compressed);
      const res = await axios.post(`${API}/upload/image`, form, { headers });
      setIntake((prev) => ({
        ...prev,
        [side === "front" ? "id_photo_front" : "id_photo_back"]: res.data.url
      }));
      toast.success("Photo uploaded");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Upload failed — try a JPG or PNG");
    } finally {
      setUploadingId(null);
    }
  };

  const [dietary, setDietary] = useState({
    vegetarian: false,
    vegan: false,
    gluten_free: false,
    allergies: "",
    other_notes: ""
  });

  const [activities, setActivities] = useState({
    yoga: false,
    fitness: false,
    hiking: false,
    cooking: false,
    music: false,
    entrepreneurship: false,
    creative_work: false,
    nature: false
  });

  const [rulesAccepted, setRulesAccepted] = useState(false);
  const [whatsappJoined, setWhatsappJoined] = useState(false);
  const [packingReviewed, setPackingReviewed] = useState(false);

  useEffect(() => {
    // Determine starting step based on onboarding progress
    if (user?.onboarding) {
      const progress = user.onboarding;
      if (progress.final_confirmed) {
        navigate("/dashboard");
        return;
      }
      
      // Find first incomplete step. Indices refer to the (possibly filtered)
      // `steps` array so volunteers land on the right starting screen even when
      // their contract/arrival/legal/packing flags were auto-completed server-side.
      const stepIdToFlag = {
        welcome: "welcome_completed",
        profile: "profile_completed",
        rules: "rules_accepted",
        arrival: "arrival_submitted",
        legal: "legal_submitted",
        stay: "stay_reason_submitted",
        rhythm: "rhythm_submitted",
        lifestyle: "lifestyle_submitted",
        packing: "packing_reviewed",
        activities: "activities_selected",
        dietary: "dietary_submitted",
        community: "community_intro_done",
        whatsapp: "whatsapp_joined",
        final: "final_confirmed",
      };

      for (let i = 0; i < steps.length; i++) {
        if (!progress[stepIdToFlag[steps[i].id]]) {
          setCurrentStep(i);
          break;
        }
      }
    }

    // Load existing data
    if (user?.profile) {
      setProfile(prev => ({ ...prev, ...user.profile }));
    }
    if (user?.arrival) {
      setArrival(prev => ({ ...prev, ...user.arrival }));
    }
    if (user?.dietary) {
      setDietary(prev => ({ ...prev, ...user.dietary }));
    }
    if (user?.activities) {
      setActivities(prev => ({ ...prev, ...user.activities }));
    }
    if (user?.intake) {
      setIntake(prev => ({ ...prev, ...user.intake }));
    }
  }, [user, navigate]);

  const headers = { Authorization: `Bearer ${token}` };
  const progress = ((currentStep + 1) / steps.length) * 100;

  const handleNext = async () => {
    setLoading(true);
    try {
      const stepId = steps[currentStep].id;
      
      switch (stepId) {
        case "welcome":
          await axios.put(`${API}/guest/onboarding/welcome_completed`, {}, { headers });
          break;
        case "profile": {
          const required = [
            [profile.avatar, "a profile photo — tap the camera to add one"],
            [profile.name, "your name"],
            [profile.date_of_birth, "your date of birth"],
            [profile.nationality, "your nationality"],
            [profile.phone, "your phone number"],
            [profile.emergency_contact_name, "your emergency contact's name"],
            [profile.emergency_contact_phone, "your emergency contact's number"],
            [profile.home_address, "your home address"]
          ];
          const missing = required.find(([v]) => !v);
          if (missing) {
            toast.error(`Please add ${missing[1]}`);
            setLoading(false);
            return;
          }
          if (!isValidIntlPhone(profile.phone)) {
            toast.error("Your phone number needs a country code (e.g. +34 612 345 678)");
            setLoading(false);
            return;
          }
          if (!isValidIntlPhone(profile.emergency_contact_phone)) {
            toast.error("The emergency contact number needs a country code (e.g. +44 7700 900123)");
            setLoading(false);
            return;
          }
          if (profile.home_address.trim().length < 10) {
            toast.error("Please enter your complete address — street & number, city, postcode, country");
            setLoading(false);
            return;
          }
          await axios.put(`${API}/guest/profile`, profile, { headers });
          break;
        }
        case "rules":
          if (!rulesAccepted) {
            toast.error("Please accept the house rules");
            setLoading(false);
            return;
          }
          await axios.put(`${API}/guest/onboarding/rules_accepted`, {}, { headers });
          break;
        case "arrival":
          if (!arrival.arrival_date || !arrival.arrival_time || !arrival.transport_method) {
            toast.error("Please fill in your arrival details");
            setLoading(false);
            return;
          }
          if (!arrival.departure_date || !arrival.room_name) {
            toast.error("Please add your departure date and room");
            setLoading(false);
            return;
          }
          if (!arrival.stay_transport) {
            toast.error("Tell us how you'll get around during your stay");
            setLoading(false);
            return;
          }
          if (arrival.stay_transport === "none" && !arrival.food_delivery_ack) {
            toast.error("Please confirm you'll arrange food delivery to the house");
            setLoading(false);
            return;
          }
          await axios.put(`${API}/guest/arrival`, arrival, { headers });
          break;
        case "legal":
          if (!intake.passport_number || !intake.id_photo_front) {
            toast.error("Please add your passport/ID number and a photo of your ID");
            setLoading(false);
            return;
          }
          await axios.put(`${API}/guest/intake`, {
            passport_number: intake.passport_number,
            visa_info: intake.visa_info,
            id_photo_front: intake.id_photo_front,
            id_photo_back: intake.id_photo_back
          }, { headers });
          await axios.put(`${API}/guest/onboarding/legal_submitted`, {}, { headers });
          break;
        case "stay":
          if (!intake.stay_reason) {
            toast.error("Please pick the reason for your stay");
            setLoading(false);
            return;
          }
          if (!intake.stay_reason_detail || intake.stay_reason_detail.trim().length < 5) {
            toast.error("Please add a short, real explanation — it goes on your contract");
            setLoading(false);
            return;
          }
          if (!intake.stay_departure_date) {
            toast.error("Please add your expected departure date");
            setLoading(false);
            return;
          }
          await axios.put(`${API}/guest/intake`, {
            stay_reason: intake.stay_reason,
            stay_reason_detail: intake.stay_reason_detail,
            stay_departure_date: intake.stay_departure_date
          }, { headers });
          await axios.put(`${API}/guest/onboarding/stay_reason_submitted`, {}, { headers });
          break;
        case "rhythm":
          await axios.put(`${API}/guest/intake`, {
            why_savi: intake.why_savi,
            coliving_experience: intake.coliving_experience,
            daily_routine: intake.daily_routine,
            remote_work: intake.remote_work,
            working_hours: intake.working_hours,
            work_needs: intake.work_needs
          }, { headers });
          await axios.put(`${API}/guest/onboarding/rhythm_submitted`, {}, { headers });
          break;
        case "lifestyle":
          await axios.put(`${API}/guest/intake`, {
            workshops: intake.workshops,
            passions: intake.passions,
            volunteering: intake.volunteering,
            music_preference: intake.music_preference,
            social_events: intake.social_events
          }, { headers });
          await axios.put(`${API}/guest/onboarding/lifestyle_submitted`, {}, { headers });
          break;
        case "packing":
          await axios.put(`${API}/guest/onboarding/packing_reviewed`, {}, { headers });
          break;
        case "activities":
          await axios.put(`${API}/guest/activities`, activities, { headers });
          break;
        case "dietary":
          await axios.put(`${API}/guest/dietary`, dietary, { headers });
          break;
        case "community":
          await axios.put(`${API}/guest/onboarding/community_intro_done`, {}, { headers });
          break;
        case "whatsapp":
          await axios.put(`${API}/guest/onboarding/whatsapp_joined`, {}, { headers });
          break;
        case "final":
          await axios.put(`${API}/guest/onboarding/final_confirmed`, {}, { headers });
          setShowConfetti(true);
          toast.success("Welcome to Savi! 🎉");
          setTimeout(() => {
            navigate("/dashboard");
          }, 2000);
          return;
      }

      await fetchUser();
      
      if (currentStep < steps.length - 1) {
        setCurrentStep(currentStep + 1);
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error(error.response?.data?.detail || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const renderStepContent = () => {
    const step = steps[currentStep];

    switch (step.id) {
      case "welcome":
        return (
          <div className="text-center space-y-6 animate-fade-in">
            <div className="w-20 h-20 mx-auto rounded-full bg-[#F4F1DE] flex items-center justify-center">
              <Sparkles className="w-10 h-10 text-[#E07A5F]" />
            </div>
            <h2 className="font-serif text-3xl md:text-4xl text-[#3D4035]">
              Welcome to Villa Savi, {user?.name}!
            </h2>
            <p className="text-[#3D4035]/60 max-w-md mx-auto leading-relaxed">
              We&apos;re excited to have you join our community. Let&apos;s get you set up with everything you need before arrival.
            </p>
            <p className="font-accent text-xl text-[#E07A5F]">
              This will only take about 5 minutes ✨
            </p>

            {/* Volunteer-only: link to the guidelines PDF. Replaces the contract
                paperwork that paying guests get on later steps. */}
            {isVolunteer && (
              <a
                href="https://customer-assets.emergentagent.com/job_5ed0a8fe-7bde-4cf0-886b-3137a44819c8/artifacts/wija5c7b_Savi%20Volunteer%20Guidelines%20%281%29.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center gap-3 bg-[#8C9A7C]/15 hover:bg-[#8C9A7C]/25 border border-[#8C9A7C]/30 rounded-2xl px-5 py-3 text-left transition-colors"
                data-testid="volunteer-guidelines-link"
              >
                <FileText className="w-5 h-5 text-[#5C6A4C] flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-[#3D4035]">Savi Team Member Guidelines</p>
                  <p className="text-xs text-[#3D4035]/55">Quick read · explains how everything works</p>
                </div>
              </a>
            )}
          </div>
        );

      case "profile":
        return (
          <div className="space-y-6 animate-fade-in">
            <div className="text-center mb-4">
              <h2 className="font-serif text-2xl md:text-3xl text-[#3D4035] mb-2">
                Tell us about yourself
              </h2>
              <p className="text-[#3D4035]/60">
                Help the community get to know you
              </p>
            </div>

            {/* The more you share, the better Savi works */}
            <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-100 rounded-2xl p-3 flex items-start gap-3" data-testid="profile-fill-hint">
              <span className="text-xl leading-none">✨</span>
              <p className="text-[13px] text-[#3D4035]/80 leading-snug">
                The more you share, the better Savi matches you with the right people in the house — your interests, your project, even a fun fact all help.
              </p>
            </div>

            {/* Avatar Upload */}
            <div className="flex justify-center mb-6">
              <div className="relative">
                <div className={`w-28 h-28 rounded-full overflow-hidden bg-[#F4F1DE] border-4 shadow-lg ${profile.avatar ? "border-white" : "border-[#E07A5F] ring-4 ring-[#E07A5F]/20 animate-pulse"}`}>
                  {profile.avatar ? (
                    <img 
                      src={profile.avatar} 
                      alt="Profile" 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <User className="w-12 h-12 text-[#3D4035]/30" />
                    </div>
                  )}
                </div>
                <label 
                  htmlFor="avatar-upload" 
                  className="absolute bottom-0 right-0 w-10 h-10 bg-[#E07A5F] rounded-full flex items-center justify-center cursor-pointer shadow-lg hover:bg-[#D06A4F] transition-colors"
                >
                  <Camera className="w-5 h-5 text-white" />
                  <input
                    id="avatar-upload"
                    type="file"
                    accept="image/*"
                    onChange={handleAvatarUpload}
                    className="hidden"
                    data-testid="avatar-upload-input"
                  />
                </label>
              </div>
            </div>
            <ImageCropDialog
              open={!!avatarCropFile}
              file={avatarCropFile}
              cropShape="round"
              title="Position your photo"
              onCancel={() => setAvatarCropFile(null)}
              onCropped={uploadAvatar}
            />
            <p className={`text-center text-sm -mt-4 mb-4 ${profile.avatar ? "text-[#3D4035]/50" : "text-[#E07A5F] font-semibold"}`}>
              {profile.avatar
                ? "Looking sharp ✨"
                : "Profile photo required — tap the camera below to add one"}
            </p>

            <div className="grid gap-4">
              <div className="space-y-2">
                <Label className="text-[#3D4035]">Full name *</Label>
                <Input
                  value={profile.name}
                  onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                  placeholder="Your name"
                  className="h-12 rounded-xl border-stone-200 bg-white"
                  data-testid="profile-name-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[#3D4035]">Preferred name / nickname</Label>
                  <Input
                    value={profile.preferred_name}
                    onChange={(e) => setProfile({ ...profile, preferred_name: e.target.value })}
                    placeholder="What should we call you?"
                    className="h-12 rounded-xl border-stone-200 bg-white"
                    data-testid="profile-preferredname-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#3D4035]">Date of birth *</Label>
                  <Input
                    type="date"
                    value={profile.date_of_birth}
                    onChange={(e) => setProfile({ ...profile, date_of_birth: e.target.value })}
                    className="h-12 rounded-xl border-stone-200 bg-white"
                    data-testid="profile-dob-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[#3D4035]">Nationality *</Label>
                  <Input
                    value={profile.nationality}
                    onChange={(e) => setProfile({ ...profile, nationality: e.target.value })}
                    placeholder="Where are you from?"
                    className="h-12 rounded-xl border-stone-200 bg-white"
                    data-testid="profile-nationality-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#3D4035]">Phone (incl. country code) *</Label>
                  <Input
                    value={profile.phone}
                    onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                    placeholder="+34 612 345 678"
                    className="h-12 rounded-xl border-stone-200 bg-white"
                    data-testid="profile-phone-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[#3D4035]">Emergency contact name *</Label>
                  <Input
                    value={profile.emergency_contact_name}
                    onChange={(e) => setProfile({ ...profile, emergency_contact_name: e.target.value })}
                    placeholder="e.g., Maria Sanchez (mum)"
                    className="h-12 rounded-xl border-stone-200 bg-white"
                    data-testid="profile-emergency-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#3D4035]">Emergency contact number *</Label>
                  <Input
                    value={profile.emergency_contact_phone}
                    onChange={(e) => setProfile({ ...profile, emergency_contact_phone: e.target.value })}
                    placeholder="+44 7700 900123"
                    className="h-12 rounded-xl border-stone-200 bg-white"
                    data-testid="profile-emergency-phone-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-[#3D4035]">Full home address *</Label>
                <Input
                  value={profile.home_address}
                  onChange={(e) => setProfile({ ...profile, home_address: e.target.value })}
                  placeholder="Street & number, city, postcode, country"
                  className="h-12 rounded-xl border-stone-200 bg-white"
                  data-testid="profile-address-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-[#3D4035]">What do you do?</Label>
                <Input
                  value={profile.occupation}
                  onChange={(e) => setProfile({ ...profile, occupation: e.target.value })}
                  placeholder="Designer, Developer, Writer..."
                  className="h-12 rounded-xl border-stone-200 bg-white"
                  data-testid="profile-occupation-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-[#3D4035]">What are you working on?</Label>
                <Textarea
                  value={profile.current_project}
                  onChange={(e) => setProfile({ ...profile, current_project: e.target.value })}
                  placeholder="Tell us about your current project..."
                  className="rounded-xl border-stone-200 bg-white min-h-[80px]"
                  data-testid="profile-project-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-[#3D4035]">Fun fact about you</Label>
                <Input
                  value={profile.fun_fact}
                  onChange={(e) => setProfile({ ...profile, fun_fact: e.target.value })}
                  placeholder="Something interesting..."
                  className="h-12 rounded-xl border-stone-200 bg-white"
                  data-testid="profile-funfact-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-[#3D4035]">Social media or website link</Label>
                <Input
                  value={profile.social_link}
                  onChange={(e) => setProfile({ ...profile, social_link: e.target.value })}
                  placeholder="Instagram, LinkedIn, your site..."
                  className="h-12 rounded-xl border-stone-200 bg-white"
                  data-testid="profile-social-link-input"
                />
              </div>

              {/* The fun stuff */}
              <div className="pt-2">
                <p className="font-medium text-[#3D4035] mb-3">The fun stuff ✨</p>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-[#3D4035]">Are you a...</Label>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setProfile({ ...profile, vibe: "morning" })}
                        className={`flex-1 py-3 px-4 rounded-xl border text-sm font-medium transition-colors ${
                          profile.vibe === "morning"
                            ? "bg-[#F2CC8F] border-[#F2CC8F] text-[#3D4035]"
                            : "bg-white border-stone-200 text-[#3D4035]/60"
                        }`}
                        data-testid="vibe-morning-btn"
                      >
                        🌅 Morning person
                      </button>
                      <button
                        type="button"
                        onClick={() => setProfile({ ...profile, vibe: "night" })}
                        className={`flex-1 py-3 px-4 rounded-xl border text-sm font-medium transition-colors ${
                          profile.vibe === "night"
                            ? "bg-[#3D4035] border-[#3D4035] text-white"
                            : "bg-white border-stone-200 text-[#3D4035]/60"
                        }`}
                        data-testid="vibe-night-btn"
                      >
                        🌙 Night owl
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#3D4035]">Your coffee (or tea) order ☕</Label>
                    <Input
                      value={profile.coffee_order}
                      onChange={(e) => setProfile({ ...profile, coffee_order: e.target.value })}
                      placeholder="e.g., Oat flat white, strong"
                      className="h-12 rounded-xl border-stone-200 bg-white"
                      data-testid="profile-coffee-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#3D4035]">A hidden talent 🎭</Label>
                    <Input
                      value={profile.hidden_talent}
                      onChange={(e) => setProfile({ ...profile, hidden_talent: e.target.value })}
                      placeholder="e.g., I can juggle / speak 4 languages / beatbox"
                      className="h-12 rounded-xl border-stone-200 bg-white"
                      data-testid="profile-talent-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#3D4035]">Ask me about... 💬</Label>
                    <Input
                      value={profile.ask_me_about}
                      onChange={(e) => setProfile({ ...profile, ask_me_about: e.target.value })}
                      placeholder="e.g., sailing, sourdough, startups, Studio Ghibli"
                      className="h-12 rounded-xl border-stone-200 bg-white"
                      data-testid="profile-askme-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#3D4035]">Song on repeat right now 🎵</Label>
                    <Input
                      value={profile.song_on_repeat}
                      onChange={(e) => setProfile({ ...profile, song_on_repeat: e.target.value })}
                      placeholder="What's your current soundtrack?"
                      className="h-12 rounded-xl border-stone-200 bg-white"
                      data-testid="profile-song-input"
                    />
                  </div>
                </div>
              </div>

              {/* Social media consent */}
              <div className="flex items-start gap-3 p-4 bg-[#F4F1DE] rounded-xl">
                <Checkbox
                  id="social-media-ok"
                  checked={profile.social_media_ok !== false}
                  onCheckedChange={(checked) => setProfile({ ...profile, social_media_ok: !!checked })}
                  className="mt-0.5 data-[state=checked]:bg-[#E07A5F] data-[state=checked]:border-[#E07A5F]"
                  data-testid="social-consent-checkbox"
                />
                <Label htmlFor="social-media-ok" className="text-[#3D4035] text-sm leading-relaxed cursor-pointer">
                  📸 I'm happy to appear in Savi's community photos on social media.
                  <span className="block text-xs text-[#3D4035]/50 mt-1">Uncheck if you'd rather stay off camera — totally fine, we'll make sure you're not in posted shots.</span>
                </Label>
              </div>

              {/* Pet Section */}
              <div className="border-t border-stone-200 pt-6 mt-6">
                <h3 className="font-medium text-[#3D4035] mb-4">🐾 Bringing a Furry Friend?</h3>
                
                    <div className="flex items-center gap-3 mb-4">
                      <Checkbox
                        id="bringing_pet"
                        checked={profile.bringing_pet}
                        onCheckedChange={(checked) => setProfile({ ...profile, bringing_pet: checked, pet_type: checked ? "dog" : "", pet_name: "", pet_breed: "", pet_photo: "" })}
                      />
                      <Label htmlFor="bringing_pet" className="text-[#3D4035]">
                        Yes, I'm bringing a pet!
                      </Label>
                    </div>

                    {profile.bringing_pet && (
                      <div className="space-y-4 bg-[#F4F1DE] rounded-xl p-4">
                        <div className="bg-[#E07A5F]/10 rounded-lg p-3 mb-2 border border-[#E07A5F]/30">
                          <p className="text-xs text-[#3D4035]">
                            <strong>Note:</strong> A pet fee of €50/week per animal will be added to your booking. Please contact us to confirm.
                          </p>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label className="text-[#3D4035]">Pet Type</Label>
                            <select
                              value={profile.pet_type}
                              onChange={(e) => setProfile({ ...profile, pet_type: e.target.value })}
                              className="w-full h-12 rounded-xl border border-stone-200 bg-white px-3"
                              data-testid="pet-type-select"
                            >
                              <option value="dog">🐕 Dog</option>
                              <option value="cat">🐱 Cat</option>
                            </select>
                          </div>
                          <div className="space-y-2">
                            <Label className="text-[#3D4035]">Pet's Name *</Label>
                            <Input
                              value={profile.pet_name}
                              onChange={(e) => setProfile({ ...profile, pet_name: e.target.value })}
                              placeholder="e.g., Luna"
                              className="h-12 rounded-xl border-stone-200 bg-white"
                              data-testid="pet-name-input"
                            />
                          </div>
                        </div>
                        
                        <div className="space-y-2">
                          <Label className="text-[#3D4035]">Breed (optional)</Label>
                          <Input
                            value={profile.pet_breed}
                            onChange={(e) => setProfile({ ...profile, pet_breed: e.target.value })}
                            placeholder="e.g., Golden Retriever"
                            className="h-12 rounded-xl border-stone-200 bg-white"
                            data-testid="pet-breed-input"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label className="text-[#3D4035]">Pet Photo (optional)</Label>
                          <div className="flex items-center gap-4">
                            {profile.pet_photo ? (
                              <div className="w-20 h-20 rounded-xl overflow-hidden">
                                <img src={profile.pet_photo} alt="Pet" className="w-full h-full object-cover" />
                              </div>
                            ) : (
                              <div className="w-20 h-20 rounded-xl bg-white border-2 border-dashed border-stone-300 flex items-center justify-center">
                                <span className="text-3xl">{profile.pet_type === "cat" ? "🐱" : "🐕"}</span>
                              </div>
                            )}
                            <label className="flex-1">
                              <input
                                type="file"
                                accept="image/*"
                                onChange={handlePetPhotoUpload}
                                className="hidden"
                              />
                              <div className="px-4 py-2 bg-white rounded-xl border border-stone-200 text-center cursor-pointer hover:bg-stone-50 transition-colors">
                                <Camera className="w-4 h-4 mx-auto mb-1 text-[#3D4035]/60" />
                                <span className="text-sm text-[#3D4035]/60">Upload Photo</span>
                              </div>
                            </label>
                          </div>
                        </div>
                      </div>
                    )}
              </div>
            </div>
          </div>
        );

      case "rules":
        return (
          <div className="space-y-6 animate-fade-in">
            <div className="text-center mb-6">
              <h2 className="font-serif text-2xl md:text-3xl text-[#3D4035] mb-2">
                House Rules
              </h2>
              <p className="font-accent text-lg text-[#E07A5F]">
                Promise not to burn the kitchen down?
              </p>
            </div>

            <div className="space-y-4">
              {[
                { title: "Sober Living", desc: "No alcohol or drugs on premises. We're building a space for clarity." },
                { title: "Respect Quiet Hours", desc: "10 PM - 8 AM. Night owls, use headphones." },
                { title: "Clean as You Go", desc: "Kitchen, bathroom, common areas. Your mom doesn't live here." },
                { title: "Be Kind to the Dogs", desc: "And to other humans. But especially the dogs." },
                { title: "Your Own Wheels", desc: "We're five minutes' drive from Náquera, so a car or scooter makes life a lot easier. There's no shopping shuttle — going without? Set up food delivery to the house." },
                { title: "Community Spirit", desc: "Join dinners, say hi, be present. Isolation isn't the vibe." }
              ].map((rule, i) => (
                <div key={i} className="bg-white rounded-xl p-4 border border-stone-100">
                  <h4 className="font-medium text-[#3D4035] mb-1">{rule.title}</h4>
                  <p className="text-sm text-[#3D4035]/60">{rule.desc}</p>
                </div>
              ))}
            </div>

            {/* Animal Policy */}
            <div className="bg-[#F4F1DE] rounded-xl p-4 border border-[#E07A5F]/20">
              <h4 className="font-medium text-[#3D4035] mb-3 flex items-center gap-2">
                🐕 Animal Policy
              </h4>
              <div className="space-y-3 text-sm text-[#3D4035]/80">
                <p><strong>Property Environment:</strong> Savi is located in a rural mountain environment. Resident dogs live on the property and may be present in outdoor areas.</p>
                <p><strong>Guest Animals:</strong> Must receive prior approval. Owners are fully responsible for their animal's behaviour, supervision, and care.</p>
                <p><strong>Main House & Kitchen:</strong> Guest animals are not permitted inside the main house or the kitchen under any circumstances. Everywhere else on site is fine.</p>
                <p><strong>Fully Fenced:</strong> The whole property is enclosed, so there's no chance of an escape and your animal can roam the grounds freely.</p>
                <p><strong>Behaviour:</strong> Animals must remain under control, not disturb others, damage property, or behave aggressively. We reserve the right to require removal of any animal causing issues.</p>
                <p><strong>Cleaning & Damage:</strong> Guests must clean up after their animal. Any damage or excessive cleaning costs will be charged.</p>
                <p><strong>Liability:</strong> Guests assume full responsibility for their animal. Savi Coliving shall not be held liable for incidents involving guest animals.</p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-4 bg-white rounded-xl border border-stone-200">
              <Checkbox
                id="rules-accept"
                checked={rulesAccepted}
                onCheckedChange={setRulesAccepted}
                className="mt-0.5 data-[state=checked]:bg-[#E07A5F] data-[state=checked]:border-[#E07A5F]"
                data-testid="rules-checkbox"
              />
              <Label htmlFor="rules-accept" className="text-[#3D4035] text-sm leading-relaxed cursor-pointer">
                I understand and agree to follow the house rules and animal policy. I'm ready to be a great community member.
              </Label>
            </div>
          </div>
        );

      case "arrival":
        return (
          <div className="space-y-6 animate-fade-in">
            <div className="text-center mb-6">
              <h2 className="font-serif text-2xl md:text-3xl text-[#3D4035] mb-2">
                Arrival Details
              </h2>
              <p className="text-[#3D4035]/60">
                Help us prepare for your arrival
              </p>
            </div>

            <div className="grid gap-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[#3D4035]">Arrival Date</Label>
                  <Input
                    type="date"
                    value={arrival.arrival_date}
                    onChange={(e) => setArrival({ ...arrival, arrival_date: e.target.value })}
                    className="h-12 rounded-xl border-stone-200 bg-white"
                    data-testid="arrival-date-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#3D4035]">Arrival Time</Label>
                  <Input
                    type="time"
                    value={arrival.arrival_time}
                    onChange={(e) => setArrival({ ...arrival, arrival_time: e.target.value })}
                    className="h-12 rounded-xl border-stone-200 bg-white"
                    data-testid="arrival-time-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[#3D4035]">Departure Date</Label>
                  <Input
                    type="date"
                    value={arrival.departure_date}
                    onChange={(e) => setArrival({ ...arrival, departure_date: e.target.value })}
                    className="h-12 rounded-xl border-stone-200 bg-white"
                    data-testid="departure-date-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#3D4035]">Your Room</Label>
                  <select
                    value={arrival.room_name}
                    onChange={(e) => setArrival({ ...arrival, room_name: e.target.value })}
                    className="w-full h-12 rounded-xl border border-stone-200 bg-white px-3 text-[#3D4035] text-sm"
                    data-testid="room-select"
                  >
                    <option value="">Which room are you in?</option>
                    {Object.values(ROOMS).map((room) => (
                      <option key={room.name} value={room.name}>{room.name}</option>
                    ))}
                    <option value="Not sure yet">Not sure yet</option>
                  </select>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-[#3D4035]">How are you getting here?</Label>
                <select
                  value={arrival.transport_method}
                  onChange={(e) => setArrival({ ...arrival, transport_method: e.target.value })}
                  className="w-full h-12 rounded-xl border border-stone-200 bg-white px-4 text-[#3D4035]"
                  data-testid="arrival-transport-select"
                >
                  <option value="">Select transport method</option>
                  <option value="taxi">Taxi from Valencia</option>
                  <option value="bus">Bus + pickup</option>
                  <option value="rental_car">Rental car</option>
                  <option value="own_car">Own car</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="space-y-2">
                <Label className="text-[#3D4035]">Flight Number (if applicable)</Label>
                <Input
                  value={arrival.flight_number}
                  onChange={(e) => setArrival({ ...arrival, flight_number: e.target.value })}
                  placeholder="e.g., VY8344"
                  className="h-12 rounded-xl border-stone-200 bg-white"
                  data-testid="arrival-flight-input"
                />
              </div>

              <div className="flex items-center gap-3 p-4 bg-[#F4F1DE] rounded-xl">
                <Checkbox
                  id="pickup"
                  checked={arrival.pickup_requested}
                  onCheckedChange={(checked) => setArrival({ ...arrival, pickup_requested: checked })}
                  className="data-[state=checked]:bg-[#E07A5F] data-[state=checked]:border-[#E07A5F]"
                  data-testid="pickup-checkbox"
                />
                <Label htmlFor="pickup" className="text-[#3D4035] text-sm cursor-pointer">
                  I'd like help arranging a pickup from Valencia
                </Label>
              </div>

              <div className="space-y-2" data-testid="arrival-stay-transport-block">
                <Label className="text-[#3D4035]">How will you get around during your stay?</Label>
                <p className="text-xs text-[#3D4035]/60 leading-relaxed">
                  We're five minutes' drive from Náquera, so most guests bring or hire a car or scooter. We don't rent cars (we do have bikes) and there's no shopping shuttle — lifts from housemates happen often, but aren't guaranteed.
                </p>
                <ChoicePills
                  options={STAY_TRANSPORT_OPTIONS}
                  value={arrival.stay_transport}
                  onChange={(v) => setArrival({ ...arrival, stay_transport: v, food_delivery_ack: v === "none" ? arrival.food_delivery_ack : false })}
                  testid="arrival-stay-transport"
                />
                {arrival.stay_transport === "none" && (
                  <div className="flex items-start gap-3 p-4 bg-[#F4F1DE] rounded-xl mt-2">
                    <Checkbox
                      id="food-delivery-ack"
                      checked={arrival.food_delivery_ack}
                      onCheckedChange={(checked) => setArrival({ ...arrival, food_delivery_ack: !!checked })}
                      className="mt-0.5 data-[state=checked]:bg-[#E07A5F] data-[state=checked]:border-[#E07A5F]"
                      data-testid="food-delivery-ack-checkbox"
                    />
                    <Label htmlFor="food-delivery-ack" className="text-[#3D4035] text-sm leading-relaxed cursor-pointer">
                      No problem — I'll arrange food delivery to the house (Consum and Mercadona both deliver) and I won't rely on lifts for shopping.
                    </Label>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-[#3D4035]">Do you plan to have any guests during your stay?</Label>
                <ChoicePills
                  options={YNM_OPTIONS}
                  value={arrival.guests_during_stay}
                  onChange={(v) => setArrival({ ...arrival, guests_during_stay: v })}
                  testid="arrival-guests"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-[#3D4035]">Need help finding long-term accommodation in Valencia after your stay?</Label>
                <ChoicePills
                  options={YNM_OPTIONS}
                  value={arrival.accommodation_help}
                  onChange={(v) => setArrival({ ...arrival, accommodation_help: v })}
                  testid="arrival-accommodation"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-[#3D4035]">Anything else we should know?</Label>
                <Textarea
                  value={arrival.notes}
                  onChange={(e) => setArrival({ ...arrival, notes: e.target.value })}
                  placeholder="Arriving late? Need directions? Let us know..."
                  className="rounded-xl border-stone-200 bg-white min-h-[80px]"
                  data-testid="arrival-notes-input"
                />
              </div>
            </div>

            <div className="bg-white rounded-xl p-4 border border-stone-100">
              <h4 className="font-medium text-[#3D4035] mb-2">📍 Getting to Savi</h4>
              <p className="text-sm text-[#3D4035]/60 mb-2">
                Address: Partida Fontanelles 2, 61, Náquera, Valencia
              </p>
              <a
                href="https://maps.google.com/?q=Partida+Fontanelles+2,+61+Náquera+Valencia"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-[#E07A5F] hover:underline"
              >
                Open in Google Maps →
              </a>
            </div>
          </div>
        );

      case "legal":
        return (
          <div className="space-y-6 animate-fade-in">
            <div className="text-center mb-6">
              <h2 className="font-serif text-2xl md:text-3xl text-[#3D4035] mb-2">
                Legal & ID
              </h2>
              <p className="text-[#3D4035]/60 max-w-md mx-auto">
                Required for guest registration with local authorities. Kept private — only Steve can see this.
              </p>
            </div>

            <div className="grid gap-4">
              <div className="space-y-2">
                <Label className="text-[#3D4035]">Passport / ID number *</Label>
                <Input
                  value={intake.passport_number}
                  onChange={(e) => setIntake({ ...intake, passport_number: e.target.value })}
                  placeholder="Document number"
                  className="h-12 rounded-xl border-stone-200 bg-white"
                  data-testid="legal-passport-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-[#3D4035]">Visa type & expiration date</Label>
                <Input
                  value={intake.visa_info}
                  onChange={(e) => setIntake({ ...intake, visa_info: e.target.value })}
                  placeholder='e.g., "Digital nomad visa, exp 03/2027" — or "EU citizen"'
                  className="h-12 rounded-xl border-stone-200 bg-white"
                  data-testid="legal-visa-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                {[
                  { side: "front", label: "ID / Passport photo *", url: intake.id_photo_front },
                  { side: "back", label: "Back side (if applicable)", url: intake.id_photo_back }
                ].map(({ side, label, url }) => (
                  <div key={side} className="space-y-2">
                    <Label className="text-[#3D4035] text-sm">{label}</Label>
                    <label className="block cursor-pointer">
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handleIdUpload(e, side)}
                        data-testid={`id-${side}-upload-input`}
                      />
                      {url ? (
                        <div className="h-32 rounded-xl overflow-hidden border border-stone-200 relative">
                          <img src={`${API.replace(/\/api$/, "")}${url}`} alt={`ID ${side}`} className="w-full h-full object-cover" />
                          <div className="absolute bottom-1 right-1 bg-white/90 rounded-full px-2 py-0.5 text-[10px] text-[#3D4035]">
                            Tap to replace
                          </div>
                        </div>
                      ) : (
                        <div className="h-32 rounded-xl bg-white border-2 border-dashed border-stone-300 flex flex-col items-center justify-center gap-1 hover:border-[#E07A5F]/50 transition-colors">
                          {uploadingId === side ? (
                            <Loader2 className="w-5 h-5 animate-spin text-[#E07A5F]" />
                          ) : (
                            <Camera className="w-5 h-5 text-[#3D4035]/40" />
                          )}
                          <span className="text-xs text-[#3D4035]/50">Upload photo</span>
                        </div>
                      )}
                    </label>
                  </div>
                ))}
              </div>

              <div className="bg-[#F4F1DE] rounded-xl p-4 text-xs text-[#3D4035]/70 leading-relaxed">
                🔒 Like hotels in Spain, we're legally required to register guests. Your documents are stored securely and never shared with other guests.
              </div>
            </div>
          </div>
        );

      case "stay":
        return (
          <div className="space-y-6 animate-fade-in" data-testid="stay-step">
            <div className="text-center mb-6">
              <h2 className="font-serif text-2xl md:text-3xl text-[#3D4035] mb-2">
                Reason for your stay
              </h2>
              <p className="text-[#3D4035]/60 max-w-md mx-auto">
                Spanish law asks that a temporary stay has a real reason behind it — it goes on your contract. An honest line is all we need.
              </p>
            </div>

            <div className="grid gap-4">
              <div className="space-y-2">
                <Label className="text-[#3D4035]">What brings you to Savi? *</Label>
                <select
                  value={intake.stay_reason}
                  onChange={(e) => setIntake({ ...intake, stay_reason: e.target.value })}
                  className="h-12 w-full rounded-xl border border-stone-200 bg-white px-3 text-[#3D4035] focus:outline-none focus:ring-2 focus:ring-[#E07A5F]/40"
                  data-testid="stay-reason-select"
                >
                  <option value="">Choose a reason…</option>
                  {STAY_REASONS.map((r) => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <Label className="text-[#3D4035]">Tell us a little more *</Label>
                <Textarea
                  value={intake.stay_reason_detail}
                  onChange={(e) => setIntake({ ...intake, stay_reason_detail: e.target.value })}
                  placeholder={'e.g. "Remote for a Berlin startup, here Nov–Feb" or "Masters at UV, enrolled this term"'}
                  className="min-h-[90px] rounded-xl border-stone-200 bg-white"
                  data-testid="stay-reason-detail"
                />
                <p className="text-xs text-[#3D4035]/50">
                  Keep it real — it goes on your contract and we may ask for a quick proof (work contract, enrolment, etc.).
                </p>
              </div>

              <div className="space-y-2">
                <Label className="text-[#3D4035]">Expected departure date *</Label>
                <Input
                  type="date"
                  value={intake.stay_departure_date}
                  onChange={(e) => setIntake({ ...intake, stay_departure_date: e.target.value })}
                  className="h-12 rounded-xl border-stone-200 bg-white"
                  data-testid="stay-departure-date"
                />
                <p className="text-xs text-[#3D4035]/50">
                  Temporary stays run from just over a month up to a year. Staying longer? Brilliant — we'll just sort the right contract.
                </p>
              </div>

              <div className="bg-[#F4F1DE] rounded-xl p-4 text-xs text-[#3D4035]/70 leading-relaxed">
                📝 Since October 2026, Spanish law treats a let as a permanent home unless there's a genuine temporary reason stated. This keeps your stay clear and above board for both of us.
              </div>
            </div>
          </div>
        );


      case "rhythm":
        return (
          <div className="space-y-6 animate-fade-in">
            <div className="text-center mb-6">
              <h2 className="font-serif text-2xl md:text-3xl text-[#3D4035] mb-2">
                Our Savi Rhythm
              </h2>
              <p className="text-[#3D4035]/60">
                Help us understand how you like to live and work
              </p>
            </div>

            <div className="grid gap-5">
              <div className="space-y-2">
                <Label className="text-[#3D4035]">In a few words, what brings you to Savi?</Label>
                <Textarea
                  value={intake.why_savi}
                  onChange={(e) => setIntake({ ...intake, why_savi: e.target.value })}
                  placeholder="Focus time, community, a reset, Valencia sunshine..."
                  className="rounded-xl border-stone-200 bg-white min-h-[70px]"
                  data-testid="rhythm-why-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-[#3D4035]">What type of coliving experience have you frequented most?</Label>
                <ChoicePills
                  options={[
                    { value: "focused", label: "Focused" },
                    { value: "social", label: "Social" },
                    { value: "professional", label: "Professional" },
                    { value: "creative", label: "Creative" },
                    { value: "wellness", label: "Wellness" },
                    { value: "first_time", label: "First time at a coliving!" }
                  ]}
                  value={intake.coliving_experience}
                  onChange={(v) => setIntake({ ...intake, coliving_experience: v })}
                  testid="rhythm-exp"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-[#3D4035]">What does your daily routine look like?</Label>
                <Textarea
                  value={intake.daily_routine}
                  onChange={(e) => setIntake({ ...intake, daily_routine: e.target.value })}
                  placeholder="e.g., early gym, deep work till 2, hike at sunset..."
                  className="rounded-xl border-stone-200 bg-white min-h-[70px]"
                  data-testid="rhythm-routine-input"
                />
              </div>

              <div className="border-t border-stone-200 pt-5 space-y-5">
                <div className="space-y-2">
                  <Label className="text-[#3D4035]">💻 Will you work remotely during your stay?</Label>
                  <ChoicePills
                    options={[
                      { value: true, label: "Yes" },
                      { value: false, label: "No" }
                    ]}
                    value={intake.remote_work}
                    onChange={(v) => setIntake({ ...intake, remote_work: v })}
                    testid="rhythm-remote"
                  />
                </div>

                {intake.remote_work !== false && (
                  <>
                    <div className="space-y-2">
                      <Label className="text-[#3D4035]">Your typical working hours</Label>
                      <Input
                        value={intake.working_hours}
                        onChange={(e) => setIntake({ ...intake, working_hours: e.target.value })}
                        placeholder="e.g., 9–5 CET, or late nights for US calls"
                        className="h-12 rounded-xl border-stone-200 bg-white"
                        data-testid="rhythm-hours-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[#3D4035]">Any special needs to make remote work smoother?</Label>
                      <Textarea
                        value={intake.work_needs}
                        onChange={(e) => setIntake({ ...intake, work_needs: e.target.value })}
                        placeholder="Monitor, standing desk, quiet call space... we'll try our best!"
                        className="rounded-xl border-stone-200 bg-white min-h-[60px]"
                        data-testid="rhythm-workneeds-input"
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        );

      case "lifestyle":
        return (
          <div className="space-y-6 animate-fade-in">
            <div className="text-center mb-6">
              <h2 className="font-serif text-2xl md:text-3xl text-[#3D4035] mb-2">
                Savi Lifestyle & Vibe
              </h2>
              <p className="text-[#3D4035]/60">
                The fun part — how do you want to show up here?
              </p>
            </div>

            <div className="grid gap-5">
              <div className="space-y-2">
                <Label className="text-[#3D4035]">What are you passionate about?</Label>
                <Input
                  value={intake.passions}
                  onChange={(e) => setIntake({ ...intake, passions: e.target.value })}
                  placeholder="e.g., climbing, AI, ceramics, breathwork..."
                  className="h-12 rounded-xl border-stone-200 bg-white"
                  data-testid="lifestyle-passions-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-[#3D4035]">Do you offer workshops? Interested in hosting one at Savi?</Label>
                <Textarea
                  value={intake.workshops}
                  onChange={(e) => setIntake({ ...intake, workshops: e.target.value })}
                  placeholder="e.g., 'I teach yoga — happy to run a Sunday class!'"
                  className="rounded-xl border-stone-200 bg-white min-h-[60px]"
                  data-testid="lifestyle-workshops-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-[#3D4035]">Up for volunteering a few hours a week to help organise events?</Label>
                <ChoicePills
                  options={[
                    { value: true, label: "Yes, count me in!" },
                    { value: false, label: "Not this time" }
                  ]}
                  value={intake.volunteering}
                  onChange={(v) => setIntake({ ...intake, volunteering: v })}
                  testid="lifestyle-volunteer"
                />
              </div>

              <div className="border-t border-stone-200 pt-5 space-y-5">
                <div className="space-y-2">
                  <Label className="text-[#3D4035]">🎵 Background music in common spaces?</Label>
                  <ChoicePills
                    options={[
                      { value: "tunes", label: "Love some tunes!" },
                      { value: "quiet", label: "I prefer quiet" }
                    ]}
                    value={intake.music_preference}
                    onChange={(v) => setIntake({ ...intake, music_preference: v })}
                    testid="lifestyle-music"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-[#3D4035]">We host 1–2 social events per week. Interested?</Label>
                  <ChoicePills
                    options={[
                      { value: "yes", label: "Yes!" },
                      { value: "sometimes", label: "One a week-ish" },
                      { value: "no", label: "Not really" }
                    ]}
                    value={intake.social_events}
                    onChange={(v) => setIntake({ ...intake, social_events: v })}
                    testid="lifestyle-events"
                  />
                </div>
              </div>
            </div>
          </div>
        );

      case "packing":
        return (
          <div className="space-y-6 animate-fade-in">
            <div className="text-center mb-6">
              <h2 className="font-serif text-2xl md:text-3xl text-[#3D4035] mb-2">
                Packing Guide
              </h2>
              <p className="text-[#3D4035]/60">
                What to bring to the mountains
              </p>
            </div>

            <div className="space-y-4">
              <div className="bg-white rounded-xl p-5 border border-stone-100">
                <h4 className="font-medium text-[#3D4035] mb-3 flex items-center gap-2">
                  <Check className="w-4 h-4 text-[#81B29A]" /> Essentials
                </h4>
                <ul className="space-y-2 text-sm text-[#3D4035]/70">
                  <li>💻 Laptop & remote work gear</li>
                  <li>👕 Comfortable clothes (casual vibe here)</li>
                  <li>🥾 Hiking shoes (trust us, you'll need them)</li>
                  <li>🧘 Yoga clothes</li>
                  <li>🩱 Swimwear (pool season: May-October)</li>
                  <li>🧥 Warm layer for mountain evenings</li>
                </ul>
              </div>

              <div className="bg-white rounded-xl p-5 border border-stone-100">
                <h4 className="font-medium text-[#3D4035] mb-3 flex items-center gap-2">
                  <Heart className="w-4 h-4 text-[#E07A5F]" /> Nice to Have
                </h4>
                <ul className="space-y-2 text-sm text-[#3D4035]/70">
                  <li>📓 Journal (for those sunset thoughts)</li>
                  <li>🎨 Creative tools (we love makers)</li>
                  <li>🎧 Good headphones</li>
                  <li>🪴 Any special toiletries</li>
                </ul>
              </div>

              <div className="bg-[#F4F1DE] rounded-xl p-5">
                <h4 className="font-medium text-[#3D4035] mb-2">Don't worry about:</h4>
                <p className="text-sm text-[#3D4035]/70">
                  Towels, bed linens, kitchen essentials, basic toiletries — we've got you covered.
                </p>
              </div>
            </div>
          </div>
        );

      case "activities":
        return (
          <div className="space-y-6 animate-fade-in">
            <div className="text-center mb-6">
              <h2 className="font-serif text-2xl md:text-3xl text-[#3D4035] mb-2">
                What interests you?
              </h2>
              <p className="text-[#3D4035]/60">
                Help us plan activities you'll love
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {[
                { key: "yoga", label: "Yoga", icon: Heart },
                { key: "fitness", label: "Fitness", icon: Dumbbell },
                { key: "hiking", label: "Hiking", icon: Mountain },
                { key: "cooking", label: "Cooking", icon: Utensils },
                { key: "music", label: "Music", icon: Music },
                { key: "entrepreneurship", label: "Business", icon: Briefcase },
                { key: "creative_work", label: "Creative", icon: Palette },
                { key: "nature", label: "Nature", icon: Leaf }
              ].map(({ key, label, icon: Icon }) => (
                <button
                  key={key}
                  onClick={() => setActivities({ ...activities, [key]: !activities[key] })}
                  className={`p-4 rounded-xl border-2 transition-all duration-300 flex flex-col items-center gap-2 ${
                    activities[key]
                      ? "border-[#E07A5F] bg-[#E07A5F]/10"
                      : "border-stone-200 bg-white hover:border-stone-300"
                  }`}
                  data-testid={`activity-${key}`}
                >
                  <Icon className={`w-6 h-6 ${activities[key] ? "text-[#E07A5F]" : "text-[#3D4035]/60"}`} />
                  <span className={`text-sm font-medium ${activities[key] ? "text-[#E07A5F]" : "text-[#3D4035]"}`}>
                    {label}
                  </span>
                </button>
              ))}
            </div>

            <p className="text-center text-sm text-[#3D4035]/60">
              Select all that apply. We'll tailor community activities to your interests.
            </p>
          </div>
        );

      case "dietary":
        return (
          <div className="space-y-6 animate-fade-in">
            <div className="text-center mb-6">
              <h2 className="font-serif text-2xl md:text-3xl text-[#3D4035] mb-2">
                Food & Kitchen
              </h2>
              <p className="text-[#3D4035]/60">
                Any dietary needs we should know about?
              </p>
            </div>

            <div className="bg-white rounded-xl p-5 border border-stone-100 mb-4">
              <h4 className="font-medium text-[#3D4035] mb-3">Kitchen at Savi</h4>
              <ul className="space-y-2 text-sm text-[#3D4035]/70">
                <li>🍳 Fully equipped shared kitchen</li>
                <li>🥗 Optional vegetarian community meals</li>
                <li>🫕 Weekly potluck dinners</li>
                <li>🛒 Local organic shop nearby</li>
                <li>🔥 BBQ nights in summer</li>
              </ul>
            </div>

            <div className="space-y-3">
              {[
                { key: "vegetarian", label: "Vegetarian" },
                { key: "vegan", label: "Vegan" },
                { key: "gluten_free", label: "Gluten Free" }
              ].map(({ key, label }) => (
                <div key={key} className="flex items-center gap-3 p-3 bg-[#F4F1DE] rounded-xl">
                  <Checkbox
                    id={key}
                    checked={dietary[key]}
                    onCheckedChange={(checked) => setDietary({ ...dietary, [key]: checked })}
                    className="data-[state=checked]:bg-[#E07A5F] data-[state=checked]:border-[#E07A5F]"
                    data-testid={`dietary-${key}`}
                  />
                  <Label htmlFor={key} className="text-[#3D4035] cursor-pointer">{label}</Label>
                </div>
              ))}
            </div>

            <div className="space-y-2">
              <Label className="text-[#3D4035]">Allergies or intolerances</Label>
              <Input
                value={dietary.allergies}
                onChange={(e) => setDietary({ ...dietary, allergies: e.target.value })}
                placeholder="e.g., nuts, shellfish..."
                className="h-12 rounded-xl border-stone-200 bg-white"
                data-testid="dietary-allergies-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-[#3D4035]">Other dietary notes</Label>
              <Textarea
                value={dietary.other_notes}
                onChange={(e) => setDietary({ ...dietary, other_notes: e.target.value })}
                placeholder="Anything else about your food preferences..."
                className="rounded-xl border-stone-200 bg-white min-h-[80px]"
                data-testid="dietary-notes-input"
              />
            </div>
          </div>
        );

      case "community":
        return (
          <div className="space-y-6 animate-fade-in">
            <div className="text-center mb-6">
              <h2 className="font-serif text-2xl md:text-3xl text-[#3D4035] mb-2">
                Meet the Community
              </h2>
              <p className="text-[#3D4035]/60">
                Your profile will be shared with other guests
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-stone-100">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-16 h-16 rounded-full bg-[#F4F1DE] flex items-center justify-center">
                  <span className="text-2xl font-serif text-[#E07A5F]">
                    {profile.name?.charAt(0) || "?"}
                  </span>
                </div>
                <div>
                  <h3 className="font-medium text-[#3D4035]">{profile.name || "Your Name"}</h3>
                  <p className="text-sm text-[#3D4035]/60">{profile.nationality || "Nationality"}</p>
                </div>
              </div>
              
              <div className="space-y-2 text-sm">
                <p className="text-[#3D4035]">
                  <span className="text-[#3D4035]/60">Doing:</span> {profile.occupation || "..."}
                </p>
                <p className="text-[#3D4035]">
                  <span className="text-[#3D4035]/60">Working on:</span> {profile.current_project || "..."}
                </p>
                <p className="text-[#3D4035]">
                  <span className="text-[#3D4035]/60">Fun fact:</span> {profile.fun_fact || "..."}
                </p>
              </div>
            </div>

            <p className="text-center text-sm text-[#3D4035]/60">
              This is how you'll appear to other community members. You can update your profile anytime.
            </p>
          </div>
        );

      case "whatsapp":
        return (
          <div className="space-y-6 animate-fade-in">
            <div className="text-center mb-6">
              <div className="w-16 h-16 mx-auto rounded-full bg-[#25D366]/10 flex items-center justify-center mb-4">
                <MessageCircle className="w-8 h-8 text-[#25D366]" />
              </div>
              <h2 className="font-serif text-2xl md:text-3xl text-[#3D4035] mb-2">
                Join the Community Chat
              </h2>
              <p className="text-[#3D4035]/60">
                Stay connected with fellow Savi members
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-stone-100 text-center">
              <p className="text-[#3D4035]/70 mb-4">
                Our WhatsApp group is where we share:
              </p>
              <ul className="text-sm text-[#3D4035]/60 space-y-2 mb-6">
                <li>📢 Community announcements</li>
                <li>🍽️ Dinner plans & potlucks</li>
                <li>🥾 Hiking & activity invites</li>
                <li>🤝 Quick questions & help</li>
              </ul>
              
              <a
                href="https://wa.me/34632628051?text=Hi%20Steve!%20I%27m%20onboarding%20for%20Savi%20and%20would%20like%20to%20join%20the%20community%20chat."
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#25D366] text-white px-6 py-3 rounded-full font-medium hover:bg-[#20BD5A] transition-colors"
                data-testid="whatsapp-join-btn"
              >
                <MessageCircle className="w-5 h-5" />
                Message Steve to Join
              </a>
            </div>

            <p className="text-center text-sm text-[#3D4035]/60">
              Steve will add you to the group. Continue to finish onboarding!
            </p>
          </div>
        );

      case "final":
        return (
          <div className="text-center space-y-6 animate-fade-in">
            <div className="w-20 h-20 mx-auto rounded-full bg-[#81B29A]/20 flex items-center justify-center">
              <PartyPopper className="w-10 h-10 text-[#81B29A]" />
            </div>
            <h2 className="font-serif text-3xl md:text-4xl text-[#3D4035]">
              You're all set!
            </h2>
            <p className="text-[#3D4035]/60 max-w-md mx-auto leading-relaxed">
              Your onboarding is complete. We can't wait to welcome you to Savi!
            </p>
            
            <div className="bg-white rounded-xl p-6 border border-stone-100 text-left space-y-3">
              <h4 className="font-medium text-[#3D4035] text-center mb-4">Quick recap:</h4>
              {[
                { label: "Profile", done: user?.onboarding?.profile_completed },
                { label: "House Rules", done: user?.onboarding?.rules_accepted },
                { label: "Arrival Details", done: user?.onboarding?.arrival_submitted },
                { label: "Activities", done: user?.onboarding?.activities_selected },
                { label: "Dietary", done: user?.onboarding?.dietary_submitted }
              ].map(({ label, done }) => (
                <div key={label} className="flex items-center gap-2">
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center ${done ? "bg-[#81B29A]" : "bg-stone-200"}`}>
                    <Check className="w-3 h-3 text-white" />
                  </div>
                  <span className="text-sm text-[#3D4035]">{label}</span>
                </div>
              ))}
            </div>

            <p className="font-accent text-xl text-[#E07A5F]">
              See you in the mountains! 🏔️
            </p>
          </div>
        );

      default:
        return null;
    }
  };

  // Confetti effect
  const Confetti = () => (
    <div className="fixed inset-0 pointer-events-none z-50">
      {[...Array(50)].map((_, i) => (
        <div
          key={i}
          className="confetti-piece"
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            backgroundColor: ["#E07A5F", "#81B29A", "#F4F1DE", "#3D4035"][Math.floor(Math.random() * 4)],
            animationDelay: `${Math.random() * 2}s`,
            borderRadius: Math.random() > 0.5 ? "50%" : "0"
          }}
        />
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FDFCF8] flex flex-col">
      {showConfetti && <Confetti />}
      
      {/* Progress Header */}
      <header className="sticky top-0 z-40 glass border-b border-stone-100">
        <div className="max-w-2xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm text-[#3D4035]/60">
              Step {currentStep + 1} of {steps.length}
              {isVolunteer && <span className="ml-2 text-[10px] uppercase tracking-wider bg-[#8C9A7C]/20 text-[#5C6A4C] px-1.5 py-0.5 rounded-full font-semibold align-middle">Team Member</span>}
            </span>
            <span className="text-sm font-medium text-[#E07A5F]">
              {steps[currentStep].title}
            </span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>
      </header>

      {/* Step indicators */}
      <div className="border-b border-stone-100 overflow-x-auto no-scrollbar">
        <div className="max-w-2xl mx-auto px-6 py-3 flex gap-2">
          {steps.map((step, i) => {
            const Icon = step.icon;
            const isComplete = i < currentStep;
            const isCurrent = i === currentStep;
            
            return (
              <div
                key={step.id}
                ref={isCurrent ? currentIconRef : null}
                className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
                  isComplete ? "bg-[#81B29A] text-white" :
                  isCurrent ? "bg-[#E07A5F] text-white" :
                  "bg-stone-100 text-stone-400"
                }`}
              >
                {isComplete ? (
                  <Check className="w-4 h-4" />
                ) : (
                  <Icon className="w-4 h-4" />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-1 max-w-2xl mx-auto w-full px-6 py-8">
        {renderStepContent()}
      </main>

      {/* Navigation */}
      <footer className="sticky bottom-0 glass border-t border-stone-100">
        <div className="max-w-2xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <Button
            variant="ghost"
            onClick={handleBack}
            disabled={currentStep === 0 || loading}
            className="rounded-full"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>

          <Button
            onClick={handleNext}
            disabled={loading}
            className="bg-[#E07A5F] hover:bg-[#D06A4F] text-white rounded-full px-8 shadow-lg"
            data-testid="next-btn"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : currentStep === steps.length - 1 ? (
              <>
                Complete
                <PartyPopper className="w-4 h-4 ml-2" />
              </>
            ) : (
              <>
                Continue
                <ArrowRight className="w-4 h-4 ml-2" />
              </>
            )}
          </Button>
        </div>
      </footer>
    </div>
  );
};

export default OnboardingWizard;
